import { z } from 'zod';
export const financeInsuranceClaimsScopeBillingCapability = 'finance.insurance-claim-scope-billing.v1';
export const financeInsuranceClaimsScopeBillingSlot = 'insurance-claims.scope-billing';
export const financeInsuranceClaimsScopeBillingProviderId = 'finance.server.insurance-claims-scope-billing';
export const financeInsuranceClaimsScopeSourceCapability = 'insurance-claims.scopes.v1';
export const financeInsuranceClaimsScopeSourceResourceType = 'insurance-claims.scope';
export const financeInsuranceClaimsScopeSourceContextType = 'insurance-claims.claim';
export const financeInsuranceClaimsScopeInvoicePurpose = 'scope-invoice';
const idSchema = z.string().uuid();
const countSchema = z.number().int().nonnegative();
const centsSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const nonvoidStatusSchema = z.enum(['draft', 'sent', 'partial', 'paid']);
export const financeInsuranceClaimsScopeBillingRequestSchema = z.strictObject({
    consumerModuleId: z.literal('insurance-claims'),
    requestedCapability: z.literal(financeInsuranceClaimsScopeBillingCapability),
    resourceType: z.literal('insurance-claims.scope'),
    resourceId: idSchema,
    claimId: idSchema,
    projectId: idSchema,
});
export const financeInsuranceClaimsScopeInvoiceFactSchema = z.strictObject({
    capability: z.literal(financeInsuranceClaimsScopeBillingCapability),
    claimId: idSchema,
    projectId: idSchema,
    scopeId: idSchema,
    invoiceId: idSchema,
    invoiceNumber: z.string().trim().min(1).max(40),
    status: nonvoidStatusSchema,
    currencyCode: z.literal('USD'),
    totalCents: centsSchema,
    amountPaidCents: centsSchema,
    amountWrittenOffCents: centsSchema,
    amountDueCents: centsSchema,
    contentHash: sha256Schema,
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    const due = value.totalCents - value.amountPaidCents - value.amountWrittenOffCents;
    if (due < 0 || due !== value.amountDueCents) {
        context.addIssue({
            code: 'custom', path: ['amountDueCents'],
            message: 'Invoice payment and balance facts must reconcile exactly.',
        });
    }
});
export const financeInsuranceClaimsScopeBillingResponseSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(financeInsuranceClaimsScopeBillingCapability),
    claimId: idSchema,
    projectId: idSchema,
    scopeId: idSchema,
    currencyCode: z.literal('USD'),
    invoiceCount: countSchema,
    byStatus: z.strictObject({
        draft: countSchema,
        sent: countSchema,
        partial: countSchema,
        paid: countSchema,
    }),
    totalBilledCents: centsSchema,
    totalPaidCents: centsSchema,
    totalWrittenOffCents: centsSchema,
    totalDueCents: centsSchema,
    invoices: z.array(financeInsuranceClaimsScopeInvoiceFactSchema).max(500),
    contentHash: sha256Schema,
    observedAt: z.string().datetime({ offset: true }).nullable(),
}).superRefine((value, context) => {
    const statusTotal = Object.values(value.byStatus).reduce((sum, count) => sum + count, 0);
    if (statusTotal !== value.invoiceCount || value.invoices.length !== value.invoiceCount) {
        context.addIssue({
            code: 'custom', path: ['invoiceCount'],
            message: 'Scope invoice counts must reconcile with status and invoice facts.',
        });
    }
    const invoiceIds = value.invoices.map(({ invoiceId }) => invoiceId);
    if (new Set(invoiceIds).size !== invoiceIds.length) {
        context.addIssue({ code: 'custom', path: ['invoices'], message: 'Invoice facts must be unique.' });
    }
    const totals = value.invoices.reduce((summary, invoice, index) => {
        if (invoice.claimId !== value.claimId || invoice.projectId !== value.projectId ||
            invoice.scopeId !== value.scopeId) {
            context.addIssue({
                code: 'custom', path: ['invoices', index],
                message: 'Invoice facts must match the requested Claim, Project, and scope.',
            });
        }
        summary.billed += invoice.totalCents;
        summary.paid += invoice.amountPaidCents;
        summary.writtenOff += invoice.amountWrittenOffCents;
        summary.due += invoice.amountDueCents;
        return summary;
    }, { billed: 0, paid: 0, writtenOff: 0, due: 0 });
    if (totals.billed !== value.totalBilledCents || totals.paid !== value.totalPaidCents ||
        totals.writtenOff !== value.totalWrittenOffCents || totals.due !== value.totalDueCents) {
        context.addIssue({
            code: 'custom', path: ['invoices'],
            message: 'Scope billing totals must equal the exact invoice facts.',
        });
    }
});
//# sourceMappingURL=finance-insurance-claims-scope-billing.contract.js.map